import { apiRequest } from '../../lib/api';
import {
  adminHealthSchema,
  adminInviteResultSchema,
  adminRecallLimitResultSchema,
  adminSignupCodeSchema,
  adminUsageSchema,
  adminUsersSchema,
  adminZoomGrantResultSchema,
  okSchema,
} from '../../lib/schemas';
import type { AdminCapabilityState, AdminHealth, AdminInvite, AdminUsage, AdminUser } from '../../types';

export interface CreateAdminUserInput extends AdminCapabilityState {
  email: string;
  username: string;
  password: string;
  org: boolean;
}

export function getAdminUsers(signal?: AbortSignal): Promise<AdminUser[]> {
  return apiRequest('/api/admin/users', adminUsersSchema, signal ? { signal } : {});
}

export async function createAdminUser(input: CreateAdminUserInput): Promise<AdminInvite> {
  const result = await apiRequest('/api/admin/users', adminInviteResultSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return result.invite;
}

export function updateAdminUser(userId: number, caps: AdminCapabilityState, approved?: boolean) {
  return apiRequest(`/api/admin/users/${userId}`, okSchema, {
    method: 'POST',
    body: JSON.stringify({ ...caps, ...(approved === undefined ? {} : { approved }) }),
  });
}

export function grantAdminZoom(userId: number, grant: boolean) {
  return apiRequest(`/api/admin/users/${userId}/grant-zoom`, adminZoomGrantResultSchema, {
    method: 'POST',
    body: JSON.stringify({ grant }),
  });
}

export function resetAdminUserPassword(userId: number, password: string) {
  return apiRequest(`/api/admin/users/${userId}/password`, okSchema, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export function deleteAdminUser(userId: number) {
  return apiRequest(`/api/admin/users/${userId}/delete`, okSchema, { method: 'POST', body: '{}' });
}

export function getAdminHealth(signal?: AbortSignal): Promise<AdminHealth> {
  return apiRequest('/api/admin/health', adminHealthSchema, signal ? { signal } : {});
}

export function runAdminHealth(): Promise<AdminHealth> {
  return apiRequest('/api/admin/health/run', adminHealthSchema, { method: 'POST' });
}

export function getAdminUsage(signal?: AbortSignal): Promise<AdminUsage> {
  return apiRequest('/api/admin/usage', adminUsageSchema, signal ? { signal } : {});
}

export function setRecallLimit(limit: number) {
  return apiRequest('/api/admin/recall-limit', adminRecallLimitResultSchema, {
    method: 'POST',
    body: JSON.stringify({ limit }),
  });
}

export function resetUsageCycle(userId: number) {
  return apiRequest(`/api/admin/usage/${userId}/reset`, okSchema, { method: 'POST', body: '{}' });
}

export function getAdminSignupCode(signal?: AbortSignal): Promise<{ code: string }> {
  return apiRequest('/api/admin/signup-code', adminSignupCodeSchema, signal ? { signal } : {});
}

export function regenerateAdminSignupCode() {
  return apiRequest('/api/admin/signup-code', adminSignupCodeSchema, {
    method: 'POST',
    body: JSON.stringify({ regenerate: true }),
  });
}
